/**
 * Static mock data for the Request flow gallery screens.
 *
 * Mirrors what the production request widget gets from the backend
 * (pre-quote options, catalog payers/states, money requests list, minted
 * fund-request link) but resolved entirely client-side so the screens render
 * with no network. Seeded into `useRequestStore` by `useSeedMockData`.
 */
import { TransactionsStatus } from "../utils/commons";
import type { ITransaction } from "../interfaces/transactions";
import {
  PayoutMethodType,
  type EstimateOption,
  type EstimateResult,
  type GenerateFundRequestLinkResult,
} from "../interfaces/payout";
import type { IRequestDraft } from "../store/useRequestStore";
import type { CountryCapability } from "../utils/catalogCountries";
import type {
  IBankAccountPaymentMethod,
  ICardPaymentMethod,
} from "../interfaces/payment-method";

const ONE_HOUR_MS = 60 * 60 * 1000;

export const REQUEST_MOCK_FX = 17.42;
export const REQUEST_MOCK_FX_CO = 4100;

export const requestDestinationCountries: CountryCapability[] = [
  {
    countryCode: "MX",
    countryName: "Mexico",
    currencyCode: "MXN",
    supportedByCorridors: ["US-MX"],
    effectiveStatus: "AVAILABLE",
  },
  {
    countryCode: "CO",
    countryName: "Colombia",
    currencyCode: "COP",
    supportedByCorridors: ["US-CO", "MX-CO"],
    effectiveStatus: "AVAILABLE",
  },
  {
    countryCode: "AR",
    countryName: "Argentina",
    currencyCode: "ARS",
    currencyCodes: ["ARS", "USD"],
    supportedByCorridors: ["US-AR"],
    effectiveStatus: "AVAILABLE",
  },
];

/** Payout methods the (mock) pre-quote found offerable for the corridor. */
export const requestAvailablePayoutMethods: PayoutMethodType[] = [
  PayoutMethodType.BANK_ACCOUNT,
  PayoutMethodType.CASH_PICKUP,
  PayoutMethodType.PUSH_TO_CARD,
  PayoutMethodType.MOBILE_WALLET,
];

const routeKey = (origin: string, dest: string) => `${origin}-${dest}`;

const requestOptionsMX: EstimateOption[] = [
  {
    capabilityKey: "cap-bank-bbva",
    corridorCode: "US-MX",
    payerCode: "BBVA",
    payoutMethod: PayoutMethodType.BANK_ACCOUNT,
    fundingMethod: "card",
    sendAmount: 100,
    receiveAmount: 1742,
    customerFee: 1,
    customerFxRate: REQUEST_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
  {
    capabilityKey: "US:MX:CRB:CASH_PICKUP:JAL:GDL:ELEKTRA",
    corridorCode: "US-MX",
    payerCode: "ELEKTRA",
    payoutMethod: PayoutMethodType.CASH_PICKUP,
    fundingMethod: "card",
    sendAmount: 100,
    receiveAmount: 1730,
    customerFee: 1.5,
    customerFxRate: REQUEST_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
  {
    capabilityKey: "US:MX:CRB:CASH_PICKUP:CMX:ANY:OXXO",
    corridorCode: "US-MX",
    payerCode: "OXXO",
    payoutMethod: PayoutMethodType.CASH_PICKUP,
    fundingMethod: "card",
    sendAmount: 100,
    receiveAmount: 1728,
    customerFee: 1.6,
    customerFxRate: REQUEST_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
  {
    capabilityKey: "cap-card",
    corridorCode: "US-MX",
    payerCode: null,
    payoutMethod: PayoutMethodType.PUSH_TO_CARD,
    fundingMethod: "card",
    sendAmount: 100,
    receiveAmount: 1738,
    customerFee: 1.2,
    customerFxRate: REQUEST_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
  {
    capabilityKey: "cap-wallet-telcel",
    corridorCode: "US-MX",
    payerCode: "TELCEL",
    payoutMethod: PayoutMethodType.MOBILE_WALLET,
    fundingMethod: "card",
    sendAmount: 100,
    receiveAmount: 1725,
    customerFee: 1.7,
    customerFxRate: REQUEST_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
];

const requestOptionsCO: EstimateOption[] = [
  {
    capabilityKey: "cap-bank-bancolombia",
    corridorCode: "US-CO",
    payerCode: "BANCOLOMBIA",
    payoutMethod: PayoutMethodType.BANK_ACCOUNT,
    fundingMethod: "card",
    sendAmount: 100,
    receiveAmount: 410000,
    customerFee: 2,
    customerFxRate: REQUEST_MOCK_FX_CO,
    sourceCurrency: "USD",
    destinationCurrency: "COP",
  },
  {
    capabilityKey: "US:CO:CRB:CASH_PICKUP:ANY:ANY:EFECTY",
    corridorCode: "US-CO",
    payerCode: "EFECTY",
    payoutMethod: PayoutMethodType.CASH_PICKUP,
    fundingMethod: "card",
    sendAmount: 100,
    receiveAmount: 408000,
    customerFee: 2.5,
    customerFxRate: REQUEST_MOCK_FX_CO,
    sourceCurrency: "USD",
    destinationCurrency: "COP",
  },
];

const requestOptionsAR: EstimateOption[] = [
  {
    capabilityKey: "cap-bank-galicia",
    corridorCode: "US-AR",
    payerCode: "GALICIA",
    payoutMethod: PayoutMethodType.BANK_ACCOUNT,
    fundingMethod: "card",
    sendAmount: 100,
    receiveAmount: 120000,
    customerFee: 2,
    customerFxRate: 1200,
    sourceCurrency: "USD",
    destinationCurrency: "ARS",
  },
];

export const requestPreQuoteByRoute: Record<string, EstimateOption[]> = {
  [routeKey("US", "MX")]: requestOptionsMX,
  [routeKey("US", "CO")]: requestOptionsCO,
  [routeKey("US", "AR")]: requestOptionsAR,
  [routeKey("MX", "CO")]: requestOptionsCO,
};

/** Static pre-quote options for the US→MX corridor (default gallery seed). */
export const requestEstimateOptions = requestOptionsMX;

export const buildRequestPreQuote = (
  originCountry: string,
  destinationCountry: string,
  amount: number
): EstimateResult => {
  const key = routeKey(originCountry, destinationCountry);
  const template =
    requestPreQuoteByRoute[key] ??
    requestPreQuoteByRoute[routeKey("US", destinationCountry)] ??
    requestEstimateOptions;
  const scale = amount / 100;
  const options = template.map((o) => ({
    ...o,
    sendAmount: Number((o.sendAmount * scale).toFixed(2)),
    receiveAmount: Number((o.receiveAmount * scale).toFixed(2)),
  }));
  const best = [...options].sort(
    (a, b) => b.receiveAmount - a.receiveAmount
  )[0];
  return {
    options,
    summary: {
      count: options.length,
      best,
      rankingMetric: "receiveAmount",
      asOf: new Date().toISOString(),
      indicative: true,
    },
  };
};

export const requestAvailablePayoutMethodsFor = (
  originCountry: string,
  destinationCountry: string,
  amount: number
): PayoutMethodType[] => {
  const { options } = buildRequestPreQuote(
    originCountry,
    destinationCountry,
    amount
  );
  const seen = new Set<PayoutMethodType>();
  for (const option of options) {
    if (
      option.payoutMethod === PayoutMethodType.BANK_ACCOUNT ||
      option.payoutMethod === PayoutMethodType.CASH_PICKUP ||
      option.payoutMethod === PayoutMethodType.PUSH_TO_CARD ||
      option.payoutMethod === PayoutMethodType.MOBILE_WALLET
    ) {
      seen.add(option.payoutMethod);
    }
  }
  return [
    PayoutMethodType.BANK_ACCOUNT,
    PayoutMethodType.CASH_PICKUP,
    PayoutMethodType.PUSH_TO_CARD,
    PayoutMethodType.MOBILE_WALLET,
  ].filter((m) => seen.has(m));
};

interface MockCatalog {
  banks: Array<{ id: string; name: string; accountPrefixes?: string[] }>;
  cashStates: Array<{ code: string; name: string }>;
  cashPayers: Array<{ id: string; name: string }>;
  carriers: Array<{ id: string; name: string }>;
}

const CATALOG_BY_COUNTRY: Record<string, MockCatalog> = {
  MX: {
    banks: [
      { id: "BBVA", name: "BBVA México", accountPrefixes: ["012"] },
      { id: "BANORTE", name: "Banorte", accountPrefixes: ["072"] },
      { id: "SANTANDER", name: "Santander", accountPrefixes: ["014"] },
      { id: "BANAMEX", name: "Citibanamex", accountPrefixes: ["002"] },
    ],
    // NLE / MEX stay in the catalog so the picker can hide states with no
    // priced cash SKU (parity with flow-request `noStates`).
    cashStates: [
      { code: "CMX", name: "Ciudad de México" },
      { code: "JAL", name: "Jalisco" },
      { code: "NLE", name: "Nuevo León" },
      { code: "MEX", name: "Estado de México" },
    ],
    cashPayers: [
      { id: "ELEKTRA", name: "Elektra" },
      { id: "OXXO", name: "Oxxo" },
      { id: "BANCOPPEL", name: "BanCoppel" },
    ],
    carriers: [
      { id: "TELCEL", name: "Telcel" },
      { id: "ATT", name: "AT&T México" },
      { id: "MOVISTAR", name: "Movistar" },
    ],
  },
  CO: {
    banks: [
      { id: "BANCOLOMBIA", name: "Bancolombia" },
      { id: "DAVIVIENDA", name: "Davivienda" },
    ],
    cashStates: [
      { code: "DC", name: "Bogotá D.C." },
      { code: "ANT", name: "Antioquia" },
    ],
    cashPayers: [
      { id: "EFECTY", name: "Efecty" },
      { id: "BALOTO", name: "Baloto" },
    ],
    carriers: [],
  },
};

const EMPTY_CATALOG: MockCatalog = {
  banks: [],
  cashStates: [],
  cashPayers: [],
  carriers: [],
};

/** Static catalog lists for a destination country (MX seeded; others empty). */
export const mockCatalogFor = (country: string): MockCatalog =>
  CATALOG_BY_COUNTRY[country?.toUpperCase()] ?? EMPTY_CATALOG;

/** Destination (push-to-card) instruments for the request instrument picker. */
export const requestDestinationCards: ICardPaymentMethod[] = [
  {
    paymentMethodId: "pm_req_visa",
    brand: "visa",
    last4: "1111",
    expiryMonth: "09",
    expiryYear: "2029",
    cardholderName: "Maria Lopez",
    enabled: true,
    isDefault: true,
  },
  {
    paymentMethodId: "pm_req_mc",
    brand: "mastercard",
    last4: "4444",
    expiryMonth: "12",
    expiryYear: "2028",
    cardholderName: "Maria Lopez",
    enabled: true,
    isDefault: false,
  },
];

/** Static cash habits shown above the cash pickup form (gallery only). */
export const requestCashPickupHabits: Array<{
  stateCode: string;
  stateName: string;
  payerId: string;
  payerDisplayName: string;
}> = [
  {
    stateCode: "JAL",
    stateName: "Jalisco",
    payerId: "ELEKTRA",
    payerDisplayName: "Elektra",
  },
  {
    stateCode: "CMX",
    stateName: "Ciudad de México",
    payerId: "OXXO",
    payerDisplayName: "Oxxo",
  },
];

/** Saved payout bank accounts for the requester (gallery). */
export const requestSavedBankAccounts: IBankAccountPaymentMethod[] = [
  {
    paymentMethodId: "pm_req_ba_bbva",
    countryCode: "MX",
    bankName: "BBVA",
    maskedAccountNumber: "****3456",
    enabled: true,
    isDefault: true,
  },
];

/** Money requests shown in the requests hub (HomePage). */
export const requestMockRequests: ITransaction[] = [
  {
    requestId: "req-gallery-funded",
    operationId: "op_gallery_request_1",
    status: TransactionsStatus.COMPLETED,
    amount: "1,742.00",
    currency: "MXN",
    date: "09/06/26",
    payerName: "Jane Doe",
  },
  {
    requestId: "req-gallery-accepted",
    operationId: "op_gallery_request_accepted",
    status: TransactionsStatus.PROCESSING,
    amount: "2,500.00",
    currency: "MXN",
    date: "07/06/26",
    payerName: "Carlos Mendoza",
  },
  {
    requestId: "req-gallery-pending",
    status: TransactionsStatus.PENDING,
    amount: "3,484.00",
    currency: "MXN",
    date: "04/06/26",
  },
  {
    requestId: "req-gallery-failed",
    status: TransactionsStatus.FAILED,
    amount: "900.00",
    currency: "MXN",
    date: "03/06/26",
  },
  {
    requestId: "req-gallery-cancelled",
    status: TransactionsStatus.CANCELED,
    amount: "600.00",
    currency: "MXN",
    date: "02/06/26",
  },
  {
    requestId: "req-gallery-expired",
    status: TransactionsStatus.EXPIRED,
    amount: "400.00",
    currency: "MXN",
    date: "01/06/26",
  },
];

/** Result of minting a shareable fund-request link. */
export const requestMockMintResult: GenerateFundRequestLinkResult = {
  url: "https://pay.leap.example/fr/abc123XYZ",
  linkId: "fr-gallery-link",
  expiresAt: new Date(Date.now() + 24 * ONE_HOUR_MS).toISOString(),
  requestId: "req-gallery-1",
};

/** A composed draft so Minting / Request Ready have context to render. */
export const requestMockDraft: IRequestDraft = {
  sourceCountry: "US",
  destinationCountry: "MX",
  sourceCurrency: "USD",
  destinationCurrency: "MXN",
  amount: 1742,
  payoutMethod: "BANK_ACCOUNT",
  payout: { bankId: "BBVA", accountNumber: "012345678901234568" },
  capabilityKey: "cap-bank-bbva",
};
